import React, { useState, useEffect, useRef } from 'react';
import Screen1 from './Screen1.js';
import { smolLM } from './SmolLMService.js';

// ── Styles ────────────────────────────────────────────────────────────────────
const G = '#4cbb17';
const DARK = '#000';

const s = {
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Courier New', monospace",
        background: DARK,
        color: G,
    },
    statusBar: {
        padding: '6px 12px',
        borderBottom: `1px solid rgba(76,187,23,0.3)`,
        fontSize: '11px',
        display: 'flex',
        justifyContent: 'space-between',
        opacity: 0.7,
        letterSpacing: '0.05em',
    },
    messages: {
        flex: 1,
        overflowY: 'auto',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    bubble: (role) => ({
        maxWidth: '82%',
        padding: '8px 12px',
        fontSize: '13px',
        lineHeight: 1.6,
        border: `1px solid rgba(76,187,23,${role === 'user' ? '0.5' : '0.25'})`,
        background: role === 'user' ? 'rgba(76,187,23,0.08)' : 'rgba(76,187,23,0.03)',
        alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
        color: G,
        textShadow: '0 0 3px rgba(76,187,23,0.3)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    }),
    prefix: {
        opacity: 0.45,
        marginRight: '6px',
        userSelect: 'none',
    },
    inputRow: {
        display: 'flex',
        gap: '6px',
        padding: '8px',
        borderTop: `1px solid rgba(76,187,23,0.3)`,
    },
    input: (disabled) => ({
        flex: 1,
        padding: '7px 10px',
        background: DARK,
        border: `1px solid rgba(76,187,23,${disabled ? '0.2' : '0.5'})`,
        color: G,
        fontFamily: "'Courier New', monospace",
        fontSize: '13px',
        outline: 'none',
        opacity: disabled ? 0.45 : 1,
        textShadow: '0 0 3px rgba(76,187,23,0.3)',
    }),
    sendBtn: (disabled) => ({
        padding: '7px 14px',
        background: disabled ? 'transparent' : G,
        border: `1px solid rgba(76,187,23,${disabled ? '0.2' : '1'})`,
        color: disabled ? `rgba(76,187,23,0.3)` : DARK,
        fontFamily: "'Courier New', monospace",
        fontSize: '12px',
        fontWeight: 'bold',
        cursor: disabled ? 'default' : 'pointer',
        letterSpacing: '0.05em',
    }),
};

// ── Download progress bar ─────────────────────────────────────────────────────
function ProgressPanel({ progress }) {
    const pct = progress?.progress ?? 0;
    const file = progress?.file ?? '';
    const status = progress?.status ?? 'initializing';

    const label = status === 'downloading'
        ? `Downloading ${file} … ${pct.toFixed(1)}%`
        : status === 'done'
            ? `Loaded ${file}`
            : status === 'ready'
                ? 'Model ready'
                : `Loading model…`;

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', gap: '20px', background: DARK, fontFamily: "'Courier New', monospace" }}>
            <div style={{ fontSize: '36px', filter: `drop-shadow(0 0 10px ${G})` }}>☘</div>
            <div style={{ fontSize: '13px', color: G, letterSpacing: '0.12em', textShadow: `0 0 8px ${G}` }}>
                S.O.S CHAT INITIALIZING
            </div>
            <div style={{ width: '100%', maxWidth: '320px' }}>
                {/* track */}
                <div style={{ height: '8px', background: 'rgba(76,187,23,0.1)', border: `1px solid rgba(76,187,23,0.3)` }}>
                    {/* fill */}
                    <div style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: G,
                        boxShadow: `0 0 8px ${G}`,
                        transition: 'width 0.3s ease',
                    }} />
                </div>
                <div style={{ marginTop: '8px', fontSize: '11px', color: G, opacity: 0.6, textAlign: 'center', letterSpacing: '0.05em' }}>
                    {label}
                </div>
            </div>
            <div style={{ fontSize: '10px', color: G, opacity: 0.4, textAlign: 'center', maxWidth: '280px', lineHeight: 1.5 }}>
                SmolLM2-360M downloads once (~180 MB) and is cached in your browser.
            </div>
        </div>
    );
}

// ── Offline panel ─────────────────────────────────────────────────────────────
function OfflinePanel({ error }) {
    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', gap: '14px', background: DARK, fontFamily: "'Courier New', monospace", textAlign: 'center' }}>
            <div style={{ fontSize: '36px', opacity: 0.4 }}>☘</div>
            <div style={{ fontSize: '13px', color: G, fontWeight: 'bold', letterSpacing: '0.1em', textShadow: `0 0 8px ${G}` }}>
                CHAT MODULE OFFLINE
            </div>
            <div style={{ fontSize: '11px', color: G, opacity: 0.6, maxWidth: '320px', lineHeight: 1.6 }}>
                {error}
            </div>
            <div style={{ fontSize: '10px', color: G, opacity: 0.35, marginTop: '8px' }}>
                Requires Chrome 113+ or Edge 113+ with WebGPU, or any browser for CPU mode.
            </div>
        </div>
    );
}

// ── iOS Safari detection ──────────────────────────────────────────────────────
function isIOSSafari() {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|OPiOS/i.test(ua);
    return isIOS && isSafari;
}

// ── Main Chat component ───────────────────────────────────────────────────────
const Chat = ({ onClose, position, zIndex, onFocus, focused }) => {
    const [phase, setPhase] = useState('loading'); // loading | ready | error
    const [progress, setProgress] = useState(null);
    const [device, setDevice] = useState(null);
    const [error, setError] = useState(null);

    const [history, setHistory] = useState([]); // [{role, content}]
    const [streamBuffer, setStreamBuffer] = useState(''); // in-flight bot text
    const [generating, setGenerating] = useState(false);
    const [input, setInput] = useState('');

    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    // Load model on first open
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const d = await smolLM.init((prog) => {
                    if (!cancelled) setProgress(prog);
                });
                if (!cancelled) {
                    setDevice(d);
                    setPhase('ready');
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.message ?? 'Failed to load model.');
                    setPhase('error');
                }
            }
        }
        // iOS Safari kills the tab under memory pressure from the model download,
        // resetting the React state machine back to the boot screen.
        if (isIOSSafari()) {
            setError('The in-browser AI model requires too much memory for iOS Safari and causes the page to restart.\n\nOpen the site in Chrome or Edge on desktop to use the chat.');
            setPhase('error');
            return;
        }
        // Only init once — if already loaded reuse it
        if (smolLM.pipe) {
            setDevice(smolLM.device);
            setPhase('ready');
        } else {
            load();
        }
        return () => { cancelled = true; };
    }, []);

    // Scroll to bottom whenever messages or stream changes
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history, streamBuffer]);

    const send = async () => {
        const text = input.trim();
        if (!text || generating) return;

        const newHistory = [...history, { role: 'user', content: text }];
        setHistory(newHistory);
        setInput('');
        setGenerating(true);
        setStreamBuffer('');

        try {
            let accumulated = '';
            await smolLM.generate(newHistory, (_chunk, full) => {
                accumulated = full;
                setStreamBuffer(full);
            });
            setHistory(prev => [...prev, { role: 'assistant', content: accumulated }]);
        } catch (e) {
            setHistory(prev => [...prev, { role: 'assistant', content: `[Error: ${e.message}]` }]);
        } finally {
            setStreamBuffer('');
            setGenerating(false);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const deviceLabel = device === 'webgpu' ? '⚡ WebGPU' : device === 'wasm' ? '🖥 CPU/WASM' : '';

    return (
        <Screen1 title="S.O.S Chat — SmolLM2-360M" onClose={onClose} position={position} zIndex={zIndex} onFocus={onFocus} focused={focused}>
            {phase === 'loading' && <ProgressPanel progress={progress} />}
            {phase === 'error'   && <OfflinePanel error={error} />}
            {phase === 'ready'   && (
                <div style={s.root}>
                    {/* Status bar */}
                    <div style={s.statusBar}>
                        <span>SmolLM2-360M-Instruct</span>
                        <span>{deviceLabel}</span>
                    </div>

                    {/* Messages */}
                    <div style={s.messages}>
                        {history.length === 0 && !generating && (
                            <div style={{ alignSelf: 'center', opacity: 0.35, fontSize: '12px', marginTop: '30px', textAlign: 'center', lineHeight: 1.8 }}>
                                ☘ S.O.S Assistant online<br />Ask me about Sean's experience, skills, or projects.
                            </div>
                        )}
                        {history.map((msg, i) => (
                            <div key={i} style={s.bubble(msg.role)}>
                                <span style={s.prefix}>{msg.role === 'user' ? '>' : '$'}</span>
                                {msg.content}
                            </div>
                        ))}
                        {/* Streaming in-flight message */}
                        {generating && (
                            <div style={s.bubble('assistant')}>
                                <span style={s.prefix}>$</span>
                                {streamBuffer || <span style={{ opacity: 0.4 }}>▋</span>}
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input row */}
                    <div style={s.inputRow}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKey}
                            placeholder={generating ? 'generating…' : '> type a message…'}
                            disabled={generating}
                            style={s.input(generating)}
                            autoFocus
                        />
                        <button
                            onClick={send}
                            disabled={generating || !input.trim()}
                            style={s.sendBtn(generating || !input.trim())}
                        >
                            SEND
                        </button>
                    </div>
                </div>
            )}
        </Screen1>
    );
};

export default Chat;
